import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';

// Add responsive styles
const styles = `
  @media (max-width: 768px) {
    .hero-title {
      font-size: 2rem !important;
    }
    .hero-description {
      font-size: 16px !important;
    }
  }
`;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { sendPasswordReset } = useAuth();
  
  const [formData, setFormData] = useState({
    email: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);

  const validateEmail = (email) => {
    const regex = /\S+@\S+\.\S+/;
    return regex.test(email);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, email: value }));
    
    // Clear error when user starts typing
    if (errors.email) {
      setErrors(prev => ({ ...prev, email: undefined }));
    }
  };

  const handleSubmit = async () => {
    setErrors({});

    // Validate email
    if (!formData.email) {
      setErrors({ email: 'Email is required' });
      return;
    }

    if (!validateEmail(formData.email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setIsSubmitting(true);

    try {
      await sendPasswordReset(formData.email);
      setIsEmailSent(true);
      toast.success('Password reset email sent! Check your inbox.');
    } catch (error) {
      console.log("Forgot password error:", error);
      
      if (error.response?.status === 400) {
        setErrors({ general: error.response.data.message });
      } else if (error.response?.status === 404) {
        setErrors({ general: 'No account found with this email address' });
      } else {
        setErrors({ general: 'Failed to send reset email. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Enter key for form submission
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  if (isEmailSent) {
    return (
      <>
        <style>{styles}</style>
        <div style={{ 
          minHeight: '100vh',
          fontFamily: 'Inter, sans-serif',
          backgroundColor: '#FEFDF6',
          width: '100%',
          overflowX: 'hidden',
          boxSizing: 'border-box',
        }}>
          {/* Hero Section */}
          <div className="hero-section" style={{
            position: 'relative',
            minHeight: '100vh',
            paddingTop: '40px',
            paddingBottom: '40px',
            overflow: 'hidden',
            background: 'linear-gradient(to bottom right, #74C0E6, #FEFDF6, rgba(189, 182, 253, 0.3))',
            width: '100%',
            boxSizing: 'border-box',
          }}>
            {/* Decorative background elements */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              zIndex: 0,
            }}>
              <div style={{
                position: 'absolute',
                top: '80px',
                left: '40px',
                width: '256px',
                height: '256px',
                backgroundColor: 'rgba(32, 150, 243, 0.2)',
                borderRadius: '50%',
                filter: 'blur(60px)',
              }}></div>
              <div style={{
                position: 'absolute',
                bottom: '80px',
                right: '40px',
                width: '384px',
                height: '384px',
                backgroundColor: 'rgba(108, 204, 22, 0.2)',
                borderRadius: '50%',
                filter: 'blur(60px)',
              }}></div>
            </div>

            {/* Hero Content */}
            <div style={{
              maxWidth: '1200px',
              margin: '0 auto',
              padding: '0 16px',
              textAlign: 'center',
              position: 'relative',
              zIndex: 10,
            }}>
              {/* Logo */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '24px',
              }}>
                <div className="logo-group" style={{
                  position: 'relative',
                  backgroundColor: '#6CCC16',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  borderBottomLeftRadius: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                }}>
                  <div style={{
                    overflow: 'hidden',
                    transition: 'all 0.5s ease-in-out',
                    maxWidth: '46px',
                  }} className="logo-text-container">
                    <span style={{
                      fontWeight: 900,
                      fontSize: '24px',
                      letterSpacing: '-0.5px',
                      whiteSpace: 'nowrap',
                      display: 'block',
                    }}>IMObituary</span>
                  </div>
                  <div style={{
                    position: 'relative',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'visible',
                    width: '24px',
                    height: '24px',
                  }}>
                    {/* Mail truck */}
                    <div className="mail-truck" style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 10,
                      opacity: 0,
                    }}>
                      <i className="fa-solid fa-truck" style={{ fontSize: '12px', color: 'white' }}></i>
                    </div>
                    {/* Envelope icons */}
                    <i className="fa-regular fa-envelope envelope-closed-animate" style={{
                      fontSize: '18px',
                      color: 'white',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 20,
                      opacity: 1,
                    }}></i>
                    <i className="fa-regular fa-envelope-open envelope-open-animate" style={{
                      fontSize: '18px',
                      color: 'white',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 20,
                      opacity: 0,
                    }}></i>
                    {/* Dollar signs */}
                    <span className="dollar-sign-1" style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 30,
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      opacity: 0,
                    }}>$</span>
                    <span className="dollar-sign-2" style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 30,
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      opacity: 0,
                    }}>$</span>
                    <span className="dollar-sign-3" style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 30,
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      opacity: 0,
                    }}>$</span>
                  </div>
                  <div style={{
                    position: 'absolute',
                    bottom: '-8px',
                    left: 0,
                    width: 0,
                    height: 0,
                    borderTop: '8px solid #6CCC16',
                    borderRight: '8px solid transparent',
                  }}></div>
                </div>
              </div>

              <h1 className="hero-title" style={{
                fontSize: '3rem',
                fontWeight: 900,
                color: '#000000',
                marginBottom: '16px',
                letterSpacing: '-1px',
                textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
              }}>
                Check Your Email
              </h1>
              <p className="hero-description" style={{
                fontSize: '20px',
                color: '#206884',
                maxWidth: '600px',
                margin: '0 auto 48px',
                fontWeight: 600,
              }}>
                We've sent a password reset link to {formData.email}
              </p>

              {/* Form Card */}
              <div style={{ 
                maxWidth: '500px',
                width: '100%',
                margin: '0 auto',
                boxSizing: 'border-box',
              }}>
                <div style={{ 
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  padding: '40px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
                  width: '100%',
                  boxSizing: 'border-box',
                  textAlign: 'left',
                }}>
                  <div style={{ 
                    backgroundColor: '#d4edda', 
                    color: '#155724', 
                    padding: '12px', 
                    borderRadius: '8px', 
                    marginBottom: '20px',
                    border: '1px solid #c3e6cb'
                  }}>
                    Check your inbox and spam folder for the reset link.
                  </div>

                  <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <p style={{ color: '#6b7280', marginBottom: '20px' }}>
                      Didn't receive the email? Try again.
                    </p>
                    
                    <button
                      type="button"
                      onClick={() => {
                        setIsEmailSent(false);
                        setFormData({ email: '' });
                      }}
                      style={{
                        width: '100%',
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        border: 'none',
                        padding: '12px',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        marginBottom: '20px',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                    >
                      Try Different Email
                    </button>
                    
                    <div>
                      <a 
                        href="/login" 
                        style={{ 
                          color: '#206884', 
                          textDecoration: 'none',
                          fontSize: '14px',
                          fontWeight: '500',
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.textDecoration = 'underline';
                          e.target.style.color = '#1a5568';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.textDecoration = 'none';
                          e.target.style.color = '#206884';
                        }}
                      >
                        Back to Login
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div style={{ 
        minHeight: '100vh',
        fontFamily: 'Inter, sans-serif',
        backgroundColor: '#FEFDF6',
        width: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box',
      }}>
        {/* Hero Section */}
        <div className="hero-section" style={{
          position: 'relative',
          minHeight: '100vh',
          paddingTop: '40px',
          paddingBottom: '40px',
          overflow: 'hidden',
          background: 'linear-gradient(to bottom right, #74C0E6, #FEFDF6, rgba(189, 182, 253, 0.3))',
          width: '100%',
          boxSizing: 'border-box',
        }}>
          {/* Decorative background elements */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            zIndex: 0,
          }}>
            <div style={{
              position: 'absolute',
              top: '80px',
              left: '40px',
              width: '256px',
              height: '256px',
              backgroundColor: 'rgba(32, 150, 243, 0.2)',
              borderRadius: '50%',
              filter: 'blur(60px)',
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '80px',
              right: '40px',
              width: '384px',
              height: '384px',
              backgroundColor: 'rgba(108, 204, 22, 0.2)',
              borderRadius: '50%',
              filter: 'blur(60px)',
            }}></div>
          </div>

          {/* Hero Content */}
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 16px',
            textAlign: 'center',
            position: 'relative',
            zIndex: 10,
          }}>
            {/* Logo */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '24px',
            }}>
              <div className="logo-group" style={{
                position: 'relative',
                backgroundColor: '#6CCC16',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '8px',
                borderBottomLeftRadius: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
              }}>
                <div style={{
                  overflow: 'hidden',
                  transition: 'all 0.5s ease-in-out',
                  maxWidth: '46px',
                }} className="logo-text-container">
                  <span style={{
                    fontWeight: 900,
                    fontSize: '24px',
                    letterSpacing: '-0.5px',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  }}>IMObituary</span>
                </div>
                <div style={{
                  position: 'relative',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'visible',
                  width: '24px',
                  height: '24px',
                }}>
                  {/* Mail truck */}
                  <div className="mail-truck" style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10,
                    opacity: 0,
                  }}>
                    <i className="fa-solid fa-truck" style={{ fontSize: '12px', color: 'white' }}></i>
                  </div>
                  {/* Envelope icons */}
                  <i className="fa-regular fa-envelope envelope-closed-animate" style={{
                    fontSize: '18px',
                    color: 'white',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 20,
                    opacity: 1,
                  }}></i>
                  <i className="fa-regular fa-envelope-open envelope-open-animate" style={{
                    fontSize: '18px',
                    color: 'white',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 20,
                    opacity: 0,
                  }}></i>
                  {/* Dollar signs */}
                  <span className="dollar-sign-1" style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 30,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    opacity: 0,
                  }}>$</span>
                  <span className="dollar-sign-2" style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 30,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    opacity: 0,
                  }}>$</span>
                  <span className="dollar-sign-3" style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 30,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    opacity: 0,
                  }}>$</span>
                </div>
                <div style={{
                  position: 'absolute',
                  bottom: '-8px',
                  left: 0,
                  width: 0,
                  height: 0,
                  borderTop: '8px solid #6CCC16',
                  borderRight: '8px solid transparent',
                }}></div>
              </div>
            </div>

            <h1 className="hero-title" style={{
              fontSize: '3rem',
              fontWeight: 900,
              color: '#000000',
              marginBottom: '16px',
              letterSpacing: '-1px',
              textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
            }}>
              Forgot Password
            </h1>
            <p className="hero-description" style={{
              fontSize: '20px',
              color: '#206884',
              maxWidth: '600px',
              margin: '0 auto 48px',
              fontWeight: 600,
            }}>
              Enter your email address and we'll send you a link to reset your password.
            </p>

            {/* Form Card */}
            <div style={{ 
              maxWidth: '500px',
              width: '100%',
              margin: '0 auto',
              boxSizing: 'border-box',
            }}>
              <div style={{ 
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '40px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
                width: '100%',
                boxSizing: 'border-box',
                textAlign: 'left',
              }}>
                <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} id="forgotform">
                  {/* Error Alert */}
                  {errors.general && (
                    <div style={{ 
                      backgroundColor: '#f8d7da', 
                      color: '#721c24', 
                      padding: '12px', 
                      borderRadius: '8px', 
                      marginBottom: '20px',
                      border: '1px solid #f5c6cb'
                    }}>
                      {errors.general}
                    </div>
                  )}

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ 
                      color: '#333', 
                      fontWeight: '500',
                      display: 'block',
                      marginBottom: '8px',
                      textAlign: 'left'
                    }}>
                      Email Address
                    </label>
                    <input 
                      type="email" 
                      name="email" 
                      placeholder="Example@example.com" 
                      value={formData.email}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      disabled={isSubmitting}
                      style={{ 
                        border: '1px solid #ddd', 
                        padding: '10px 15px',
                        width: '100%',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }} 
                      required 
                      autoFocus
                    />
                    {errors.email && (
                      <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                        {errors.email}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    style={{
                      width: '100%',
                      backgroundColor: '#206884',
                      color: 'white',
                      border: 'none',
                      padding: '12px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      opacity: isSubmitting ? 0.7 : 1,
                      marginBottom: '20px',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubmitting) {
                        e.target.style.backgroundColor = '#1a5568';
                        e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSubmitting) {
                        e.target.style.backgroundColor = '#206884';
                        e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                      }
                    }}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Reset Link'}
                  </button>

                  {/* Links */}
                  <div style={{ 
                    marginTop: '30px', 
                    textAlign: 'center'
                  }}>
                    <a 
                      href="/login" 
                      style={{ 
                        color: '#206884', 
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.textDecoration = 'underline';
                        e.target.style.color = '#1a5568';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.textDecoration = 'none';
                        e.target.style.color = '#206884';
                      }}
                    >
                      Back to Login
                    </a>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPasswordPage;

